import { useTheme } from "../../context/ThemeContext";
import { formatAddress, formatAmount } from "../../utils";
import { MoneyIcon, WalletIcon, ArrowDownIcon, PiggyBankIcon, MoneyBoxIcon, RecycleIcon } from "../SVGIcons";
import OverViewItemCard from "../OverViewItemCard";

const ClipboardIcon = ({ fillColor = '#888' }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="7" y="3" width="10" height="4" rx="2" fill={fillColor} />
    <rect x="5" y="7" width="14" height="14" rx="2" stroke={fillColor} strokeWidth="2" fill="none" />
  </svg>
);
const CheckIcon = ({ fillColor = '#4ade80' }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M5 13l4 4L19 7" stroke={fillColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const TraderDetailOverview: React.FC<{
  traderData: any;
  handleCopyAddress: (address: string) => void;
  copiedAddress: string;
  id: string;
  balance: string;
}> = ({ traderData, handleCopyAddress, copiedAddress, id, balance }) => {
  const { theme } = useTheme();

  if (!traderData) {
    return (
      <div className="bg-white dark:bg-black rounded-lg">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <OverViewItemCard
            cardView={<WalletIcon fillColor={theme === 'dark' ? '#fff' : '#000'} />}
            value={
              <div className="animate-pulse">
                <div className="h-[32px] bg-gray-300 dark:bg-gray-600 rounded w-20"></div>
              </div>
            }
            title="Address"
            iconColor={theme === 'dark' ? "bg-[#262626]" : "bg-[#F2F2F2]"}
            textColor={theme === 'dark' ? "text-white" : "text-black"}
          />
          <OverViewItemCard
            cardView={<MoneyIcon fillColor={theme === 'dark' ? '#fff' : '#000'} />}
            value={
              <div className="animate-pulse">
                <div className="h-[32px] bg-gray-300 dark:bg-gray-600 rounded w-16"></div>
              </div>
            }
            title="Balance"
            iconColor={theme === 'dark' ? "bg-[#262626]" : "bg-[#F2F2F2]"}
            textColor={theme === 'dark' ? "text-white" : "text-black"}
          />
          <OverViewItemCard
            cardView={<ArrowDownIcon fillColor={theme === 'dark' ? '#fff' : '#000'} />}
            value={
              <div className="animate-pulse">
                <div className="h-[32px] bg-gray-300 dark:bg-gray-600 rounded w-16"></div>
              </div>
            }
            title="Collateral"
            iconColor={theme === 'dark' ? "bg-[#262626]" : "bg-[#F2F2F2]"}
            textColor={theme === 'dark' ? "text-white" : "text-black"}
          />
          <OverViewItemCard
            cardView={<MoneyBoxIcon fillColor={theme === 'dark' ? '#fff' : '#000'} />}
            value={
              <div className="animate-pulse">
                <div className="h-[32px] bg-gray-300 dark:bg-gray-600 rounded w-20"></div>
              </div>
            }
            title="Borrow Available"
            iconColor={theme === 'dark' ? "bg-[#262626]" : "bg-[#F2F2F2]"}
            textColor={theme === 'dark' ? "text-white" : "text-black"}
          />
          <OverViewItemCard
            cardView={<PiggyBankIcon fillColor={theme === 'dark' ? '#fff' : '#000'} />}
            value={
              <div className="animate-pulse">
                <div className="h-[32px] bg-gray-300 dark:bg-gray-600 rounded w-12"></div>
              </div>
            }
            title="Staked"
            iconColor={theme === 'dark' ? "bg-[#262626]" : "bg-[#F2F2F2]"}
            textColor={theme === 'dark' ? "text-white" : "text-black"}
          />
          <OverViewItemCard
            cardView={<PiggyBankIcon fillColor={theme === 'dark' ? '#fff' : '#000'} />}
            value={
              <div className="animate-pulse">
                <div className="h-[32px] bg-gray-300 dark:bg-gray-600 rounded w-20"></div>
              </div>
            }
            title="Current Stakes"
            iconColor={theme === 'dark' ? "bg-[#262626]" : "bg-[#F2F2F2]"}
            textColor={theme === 'dark' ? "text-white" : "text-black"}
          />
          <OverViewItemCard
            cardView={<MoneyBoxIcon fillColor={theme === 'dark' ? '#fff' : '#000'} />}
            value={
              <div className="animate-pulse">
                <div className="h-[32px] bg-gray-300 dark:bg-gray-600 rounded w-24"></div>
              </div>
            }
            title="Borrowed Staked Amount"
            iconColor={theme === 'dark' ? "bg-[#262626]" : "bg-[#F2F2F2]"}
            textColor={theme === 'dark' ? "text-white" : "text-black"}
          />
          <OverViewItemCard
            cardView={<RecycleIcon fillColor={theme === 'dark' ? '#fff' : '#000'} />}
            value={
              <div className="animate-pulse">
                <div className="h-[32px] bg-gray-300 dark:bg-gray-600 rounded w-24"></div>
              </div>
            }
            title="Pending Borrow Available"
            iconColor={theme === 'dark' ? "bg-[#262626]" : "bg-[#F2F2F2]"}
            textColor={theme === 'dark' ? "text-white" : "text-black"}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-black rounded-lg">

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <OverViewItemCard
          cardView={<WalletIcon fillColor={theme === 'dark' ? '#fff' : '#000'} />}
          value={
            <span className="flex items-center gap-2">
              {formatAddress(id)}
              <button
                onClick={() => handleCopyAddress(id)}
                className="ml-1 p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition"
                title={copiedAddress === id ? 'Copied!' : 'Copy address'}
                style={{ lineHeight: 0 }}
              >
                {copiedAddress === id ? <CheckIcon fillColor="#4ade80" /> : <ClipboardIcon fillColor={theme === 'dark' ? '#fff' : '#000'} />}
              </button>
            </span>
          }
          title="Address"
          iconColor={theme === 'dark' ? "bg-[#262626]" : "bg-[#F2F2F2]"}
          textColor={theme === 'dark' ? "text-white" : "text-black"}
        />
        <OverViewItemCard cardView={<MoneyIcon fillColor={theme === 'dark' ? '#fff' : '#000'} />} value={balance ? 'τ' + formatAmount((Number(balance.replace(/,/g, ''))).toString()) : 'Unknown'} title="Balance" iconColor={theme === 'dark' ? "bg-[#262626]" : "bg-[#F2F2F2]"} textColor={theme === 'dark' ? "text-white" : "text-black"} />
        <OverViewItemCard cardView={<ArrowDownIcon fillColor={theme === 'dark' ? '#fff' : '#000'} />} value={`τ${formatAmount(traderData?.collateral_amount?.toString() || "0")}`} title="Collateral" iconColor={theme === 'dark' ? "bg-[#262626]" : "bg-[#F2F2F2]"} textColor={theme === 'dark' ? "text-white" : "text-black"} />
        <OverViewItemCard cardView={<MoneyBoxIcon fillColor={theme === 'dark' ? '#fff' : '#000'} />} value={`τ${formatAmount(traderData?.available_amount?.toString() || "0")}`} title="Borrow Available" iconColor={theme === 'dark' ? "bg-[#262626]" : "bg-[#F2F2F2]"} textColor={theme === 'dark' ? "text-white" : "text-black"} />
        <OverViewItemCard cardView={<PiggyBankIcon fillColor={theme === 'dark' ? '#fff' : '#000'} />} value={`τ${formatAmount(traderData?.active_stakes_sum?.toString() || "0")}`} title="Staked" iconColor={theme === 'dark' ? "bg-[#262626]" : "bg-[#F2F2F2]"} textColor={theme === 'dark' ? "text-white" : "text-black"} />
        <OverViewItemCard cardView={<PiggyBankIcon fillColor={theme === 'dark' ? '#fff' : '#000'} />} value={`τ${formatAmount(traderData?.current_stakes_amount?.toString() || "0")}`} title="Current Stakes" iconColor={theme === 'dark' ? "bg-[#262626]" : "bg-[#F2F2F2]"} textColor={theme === 'dark' ? "text-white" : "text-black"} />
        <OverViewItemCard cardView={<MoneyBoxIcon fillColor={theme === 'dark' ? '#fff' : '#000'} />} value={`τ${formatAmount(traderData?.borrowed_stakes_amount?.toString() || "0")}`} title="Borrowed Staked Amount" iconColor={theme === 'dark' ? "bg-[#262626]" : "bg-[#F2F2F2]"} textColor={theme === 'dark' ? "text-white" : "text-black"} />
        <OverViewItemCard cardView={<RecycleIcon fillColor={theme === 'dark' ? '#fff' : '#000'} />} value={`τ${Math.abs(Number(traderData?.available_pending_amount)) < 1e-10 ? '0.000' : formatAmount(traderData?.available_pending_amount?.toString() || "0")}`} title="Pending Borrow Available" iconColor={theme === 'dark' ? "bg-[#262626]" : "bg-[#F2F2F2]"} textColor={theme === 'dark' ? "text-white" : "text-black"} />
      </div>
    </div>
  );
};

export default TraderDetailOverview;